import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PrimaryButton from "src/components/atoms/PrimaryButton";
import { deptColor, formatDate, formatSalary } from "src/utils/func/vacancyDeptColor";
import { X, ExternalLink, IndianRupee, Calendar, Building2, Tag } from "lucide-react";
import Image from "next/image";

const VacancyModal = ({ vacancy, onClose, onApplyClick }) => {
  if (!vacancy) return null;
  const color = deptColor(vacancy.department);
  useEffect(() => {
    const scrollY = window.scrollY;

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";

      window.scrollTo(0, scrollY);
    };
  }, []);
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] flex items-center justify-center !p-4 bg-black/60 backdrop-blur-md"
      >
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-7xl overflow-hidden rounded-lg border border-border-primary shadow-2xl"
          style={{ background: "var(--c-surface)" }}
        >
          <div className="relative h-56 overflow-hidden">
            {vacancy.jobPoster ? (
              <Image src={vacancy.jobPoster} alt={vacancy.title} fill className="absolute inset-0 object-cover object-center" />
            ) : (
              <div className="absolute inset-0 w-full h-full" style={{ background: `linear-gradient(135deg, ${color.bg} 0%, ${color.border} 100%)` }} />
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />

            <button
              onClick={onClose}
              aria-label="Close modal"
              className="  absolute top-3 right-3  w-9 h-9 rounded-full  flex items-center justify-center  bg-white/10 hover:bg-white/20  border border-white/20
                text-white  transition-all duration-150  cursor-pointer  backdrop-blur-sm  "
            >
              <X size={16} />
            </button>

            <div className="absolute bottom-0 left-0 right-0 !p-6 !pb-5">
              <div className="flex flex-wrap !gap-2 !mb-3">
                <span
                  className="inline-flex items-center !gap-1.5 !px-3 !py-1 rounded-full text-[11px] font-semibold tracking-wide"
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    color: "#fff",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <Building2 size={11} />
                  {vacancy.department}
                </span>

                <span
                  className="inline-flex items-center !gap-1.5 !px-3 !py-1 rounded-full text-[11px] font-semibold tracking-wide"
                  style={{
                    background: "rgba(29,198,130,0.18)",
                    border: "1px solid rgba(29,198,130,0.35)",
                    color: "#6ee7b7",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <Tag size={11} />
                  {vacancy.category}
                </span>
              </div>

              <h2 className="text-2xl font-semibold text-white tracking-tight leading-tight !m-0 !mb-3">{vacancy.title}</h2>

              <div className="flex flex-wrap !gap-x-5 !gap-y-1.5">
                <span className="inline-flex items-center !gap-1.5 text-[12px] text-white/70">
                  <IndianRupee size={13} className="text-emerald-400" />
                  <span className="text-white font-medium">₹{formatSalary(vacancy.salaryRangeMin, vacancy.salaryRangeMax)}</span>
                  &nbsp;/ yr
                </span>

                <span className="inline-flex items-center !gap-1.5 text-[12px] text-white/70">
                  <Calendar size={13} className="text-blue-400" />
                  Posted&nbsp;
                  <span className="text-white font-medium">{formatDate(vacancy.vacancyDate)}</span>
                </span>

                {vacancy.location && (
                  <span className="inline-flex items-center !gap-1.5 text-[12px] text-white/70">
                    <MapPin size={13} className="text-rose-400" />
                    <span className="text-white font-medium">{vacancy.location}</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="!p-8 max-h-[45vh] overflow-y-auto modern-scroll">
            <div className="prose dark:prose-invert max-w-none text-text-primary" dangerouslySetInnerHTML={{ __html: vacancy.description }} />
          </div>

          <div className="!p-6 border-t border-border-primary flex items-center justify-center">
            <PrimaryButton size="lg" onClick={onApplyClick} icon={<ExternalLink />} className="!px-10 !py-2">
              Apply Now
            </PrimaryButton>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VacancyModal;
